// Telex BP-1002 Beltpack — Tier 2 vector-fidelity intercom panel.
//
// Single-channel Audiocom intercom beltpack used by the AV crew. The
// SVG reproduces the top-facing control surface — the face the operator
// sees when the unit is clipped to a belt and looked down at.
//
// Dimensions per the manual (BP-1002/2002 Operating Instructions,
// 9350-7740-000 Rev K, August 2008): the unit body is 127mm × 45.7mm
// on the control face. The viewBox is locked to that aspect ratio so
// the panel cannot be visually distorted by responsive CSS — proportions
// are sacred per the Tier 2 fidelity contract.
//
// Front-face controls (left to right per Figure 1 of the manual):
//   1. Volume knob (R145-coupled, drives headset listen level)
//   3. Call LED + Call button — red indicator; momentary send + receive
//   4. Talk LED + Talk button — green indicator; latch or momentary
//   "Telex" Audiocom wordmark — printed on the right side of the face
//
// The BP-2002 (two-channel) adds a Chan button between knob and Call;
// that variant belongs in a separate file.
//
// Doctrine: ADR-0015 §10 (interactive single-user simulators).
// Fidelity contract: docs/insights/tier-2-vector-fidelity.md.
// Source: docs/sources/equipment/telex-bp-1002-2002-manual.pdf.

import { useCallback, useEffect, useRef, useState } from "react";
import { HelpShell } from "./help-shell";

export type TalkMode = "latch" | "momentary";

export interface TelexBP1002SeatState {
  talk: boolean;
  call: boolean;
  volume: number;
}

export type TelexBP1002Action =
  | { kind: "talk_on"; mode: TalkMode; ts_ms: number }
  | { kind: "talk_off"; mode: TalkMode; ts_ms: number }
  | { kind: "call_on"; ts_ms: number }
  | { kind: "call_off"; ts_ms: number }
  | { kind: "volume"; value: number; ts_ms: number };

export interface TelexBP1002Props {
  /**
   * Latch = single tap toggles on/off. Momentary = press-and-hold.
   * The real unit supports both via internal mode setting; mode 2
   * (factory default) is latch + mic-kill, so latch is the default here.
   * `initialState.talkMode` (passed by `EquipmentSimulator`) wins over
   * this top-level prop when both are present.
   */
  talkMode?: TalkMode;
  initialState?: {
    talk?: boolean;
    call?: boolean;
    volume?: number;
    talkMode?: TalkMode;
  };
  onStateChange?: (state: TelexBP1002SeatState) => void;
  onAction?: (action: TelexBP1002Action) => void;
}

export default function TelexBP1002({
  talkMode: talkModeProp,
  initialState,
  onStateChange,
  onAction,
}: TelexBP1002Props = {}) {
  const talkMode: TalkMode = initialState?.talkMode ?? talkModeProp ?? "latch";
  const [talk, setTalk] = useState(initialState?.talk ?? false);
  const [call, setCall] = useState(initialState?.call ?? false);
  const [volume, setVolume] = useState(
    Math.min(100, Math.max(0, initialState?.volume ?? 60)),
  );

  useEffect(() => {
    onStateChange?.({ talk, call, volume });
  }, [talk, call, volume, onStateChange]);

  const pressTalk = useCallback(() => {
    if (talkMode === "latch") {
      setTalk((prev) => {
        const next = !prev;
        onAction?.({
          kind: next ? "talk_on" : "talk_off",
          mode: "latch",
          ts_ms: Date.now(),
        });
        return next;
      });
    } else {
      setTalk(true);
      onAction?.({ kind: "talk_on", mode: "momentary", ts_ms: Date.now() });
    }
  }, [talkMode, onAction]);

  const releaseTalk = useCallback(() => {
    if (talkMode === "momentary") {
      setTalk(false);
      onAction?.({ kind: "talk_off", mode: "momentary", ts_ms: Date.now() });
    }
  }, [talkMode, onAction]);

  const pressCall = useCallback(() => {
    setCall(true);
    onAction?.({ kind: "call_on", ts_ms: Date.now() });
  }, [onAction]);

  const releaseCall = useCallback(() => {
    setCall(false);
    onAction?.({ kind: "call_off", ts_ms: Date.now() });
  }, [onAction]);

  const adjustVolume = useCallback(
    (delta: number) => {
      setVolume((prev) => {
        const next = Math.min(100, Math.max(0, Math.round(prev + delta)));
        if (next !== prev) {
          onAction?.({ kind: "volume", value: next, ts_ms: Date.now() });
        }
        return next;
      });
    },
    [onAction],
  );

  // Rotary drag — mouse + touch via pointer events. Tracks the angle
  // from the knob's center to the pointer; the change in that angle
  // (mod 360, with wrap-around handling) maps onto volume. 270° of
  // pointer rotation = full 0..100 sweep, matching the visual indicator.
  const knobRef = useRef<SVGGElement | null>(null);
  const dragRef = useRef<{ pointerId: number; lastAngle: number } | null>(null);

  const angleToPointer = useCallback((clientX: number, clientY: number): number | null => {
    const node = knobRef.current;
    if (!node) return null;
    const rect = node.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    return (Math.atan2(clientY - cy, clientX - cx) * 180) / Math.PI;
  }, []);

  const handleKnobPointerDown = useCallback(
    (e: React.PointerEvent<SVGGElement>) => {
      const angle = angleToPointer(e.clientX, e.clientY);
      if (angle === null) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = { pointerId: e.pointerId, lastAngle: angle };
      e.preventDefault();
    },
    [angleToPointer],
  );

  const handleKnobPointerMove = useCallback(
    (e: React.PointerEvent<SVGGElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      const angle = angleToPointer(e.clientX, e.clientY);
      if (angle === null) return;
      let delta = angle - drag.lastAngle;
      if (delta > 180) delta -= 360;
      else if (delta < -180) delta += 360;
      // 270° of rotation = full 0..100 range.
      const volumeDelta = (delta / 270) * 100;
      if (Math.abs(volumeDelta) >= 0.5) {
        adjustVolume(volumeDelta);
        drag.lastAngle = angle;
      }
    },
    [angleToPointer, adjustVolume],
  );

  const handleKnobPointerUp = useCallback(
    (e: React.PointerEvent<SVGGElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      e.currentTarget.releasePointerCapture(e.pointerId);
      dragRef.current = null;
    },
    [],
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return;
      if (e.repeat) return;
      const key = e.key.toLowerCase();
      if (key === "t") {
        e.preventDefault();
        pressTalk();
      } else if (key === "c") {
        e.preventDefault();
        pressCall();
      } else if (key === "arrowup") {
        e.preventDefault();
        adjustVolume(5);
      } else if (key === "arrowdown") {
        e.preventDefault();
        adjustVolume(-5);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (key === "t") releaseTalk();
      else if (key === "c") releaseCall();
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [pressTalk, releaseTalk, pressCall, releaseCall, adjustVolume]);

  // Knob rotation: -135° at volume=0, +135° at volume=100 (270° total sweep).
  const knobAngle = -135 + (volume / 100) * 270;

  return (
    <HelpShell panelKind="telex-bp-1002">
      <div
        style={{
          width: "100%",
          maxWidth: 760,
          aspectRatio: "1270 / 457",
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1270 457"
          role="img"
          aria-label="Telex BP-1002 single-channel intercom beltpack"
          style={{ width: "100%", height: "100%", display: "block" }}
        >
          <defs>
            <linearGradient id="bp1002-body" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#2c2c2c" />
              <stop offset="40%" stopColor="#161616" />
              <stop offset="100%" stopColor="#080808" />
            </linearGradient>
            <linearGradient id="bp1002-edge" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3a3a3a" />
              <stop offset="100%" stopColor="#080808" />
            </linearGradient>
            <radialGradient id="bp1002-knob" cx="0.42" cy="0.38" r="0.7">
              <stop offset="0%" stopColor="#e2d2b4" />
              <stop offset="55%" stopColor="#b39872" />
              <stop offset="100%" stopColor="#665236" />
            </radialGradient>
            <radialGradient id="bp1002-knob-cap" cx="0.5" cy="0.5" r="0.6">
              <stop offset="0%" stopColor="#a48a5e" />
              <stop offset="100%" stopColor="#5e4c30" />
            </radialGradient>
            <linearGradient id="bp1002-button" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3c3c3c" />
              <stop offset="100%" stopColor="#101010" />
            </linearGradient>
            <linearGradient id="bp1002-button-pressed" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0c0c0c" />
              <stop offset="100%" stopColor="#2c2c2c" />
            </linearGradient>
            <radialGradient id="led-red" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="#ff5050" />
              <stop offset="55%" stopColor="#cc1010" />
              <stop offset="100%" stopColor="#5c0606" />
            </radialGradient>
            <radialGradient id="led-green" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="#80ff80" />
              <stop offset="55%" stopColor="#15c020" />
              <stop offset="100%" stopColor="#053808" />
            </radialGradient>
            <filter id="led-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="6" />
            </filter>
          </defs>

          {/* Outer bezel + face plate. */}
          <rect x="4" y="4" width="1262" height="449" rx="36" ry="36" fill="url(#bp1002-edge)" />
          <rect x="14" y="14" width="1242" height="429" rx="30" ry="30" fill="url(#bp1002-body)" />
          <rect x="40" y="22" width="1190" height="2" rx="1" fill="#3a3a3a" opacity="0.55" />

          {/* Volume knob — drag (mouse/touch), scroll wheel, or arrow keys. */}
          <g
            ref={knobRef}
            transform="translate(190, 228)"
            data-hotspot-id="volume-knob"
            onPointerDown={handleKnobPointerDown}
            onPointerMove={handleKnobPointerMove}
            onPointerUp={handleKnobPointerUp}
            onPointerCancel={handleKnobPointerUp}
            onWheel={(e) => {
              e.preventDefault();
              adjustVolume(e.deltaY < 0 ? 4 : -4);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowUp" || e.key === "ArrowRight") {
                e.preventDefault();
                adjustVolume(5);
              } else if (e.key === "ArrowDown" || e.key === "ArrowLeft") {
                e.preventDefault();
                adjustVolume(-5);
              }
            }}
            style={{ cursor: "grab", touchAction: "none" }}
            role="slider"
            aria-label="Headset volume"
            aria-valuenow={volume}
            aria-valuemin={0}
            aria-valuemax={100}
            tabIndex={0}
          >
            <circle cx="0" cy="6" r="135" fill="#000" opacity="0.55" />
            <circle cx="0" cy="0" r="135" fill="url(#bp1002-knob)" stroke="#3a2a18" strokeWidth="2" />
            <g transform={`rotate(${knobAngle})`}>
              <rect x="-6" y="-128" width="12" height="44" rx="3" fill="#1a1208" />
            </g>
            <circle cx="0" cy="0" r="42" fill="url(#bp1002-knob-cap)" stroke="#5a4528" strokeWidth="1" />
          </g>

          {/* Call LED. */}
          <circle cx="450" cy="228" r="22" fill="#0a0a0a" />
          <circle cx="450" cy="228" r="16" fill="url(#led-red)" opacity={call ? 1 : 0.28} />
          {call ? (
            <circle cx="450" cy="228" r="38" fill="#ff3030" opacity="0.45" filter="url(#led-glow)" />
          ) : null}

          {/* Call button (sharp-cornered rectangle). */}
          <g
            data-hotspot-id="call-btn"
            onMouseDown={pressCall}
            onMouseUp={releaseCall}
            onMouseLeave={releaseCall}
            onTouchStart={(e) => {
              e.preventDefault();
              pressCall();
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              releaseCall();
            }}
            style={{ cursor: "pointer" }}
            role="button"
            aria-label="Call"
            aria-pressed={call}
            tabIndex={0}
          >
            <rect
              x="500"
              y="170"
              width="190"
              height="116"
              rx="8"
              fill={call ? "url(#bp1002-button-pressed)" : "url(#bp1002-button)"}
              stroke="#000"
              strokeWidth="1"
            />
            <text
              x="595"
              y="250"
              textAnchor="middle"
              fontFamily="Georgia, 'Times New Roman', serif"
              fontStyle="italic"
              fontWeight="700"
              fontSize="58"
              fill="#d8c890"
            >
              Call
            </text>
          </g>

          {/* Talk LED. */}
          <circle cx="760" cy="228" r="22" fill="#0a0a0a" />
          <circle cx="760" cy="228" r="16" fill="url(#led-green)" opacity={talk ? 1 : 0.28} />
          {talk ? (
            <circle cx="760" cy="228" r="38" fill="#30ff40" opacity="0.45" filter="url(#led-glow)" />
          ) : null}

          {/* Talk button (rounded corners — slightly softer than Call). */}
          <g
            data-hotspot-id="talk-btn"
            onMouseDown={pressTalk}
            onMouseUp={releaseTalk}
            onMouseLeave={releaseTalk}
            onTouchStart={(e) => {
              e.preventDefault();
              pressTalk();
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              releaseTalk();
            }}
            style={{ cursor: "pointer" }}
            role="button"
            aria-label="Talk"
            aria-pressed={talk}
            tabIndex={0}
          >
            <rect
              x="810"
              y="170"
              width="190"
              height="116"
              rx="22"
              fill={talk ? "url(#bp1002-button-pressed)" : "url(#bp1002-button)"}
              stroke="#000"
              strokeWidth="1"
            />
            <text
              x="905"
              y="250"
              textAnchor="middle"
              fontFamily="Georgia, 'Times New Roman', serif"
              fontStyle="italic"
              fontWeight="700"
              fontSize="58"
              fill="#d8c890"
            >
              Talk
            </text>
          </g>

          {/* Telex Audiocom wordmark — italic gold on the right. */}
          <text
            x="1140"
            y="258"
            textAnchor="middle"
            fontFamily="Georgia, 'Times New Roman', serif"
            fontStyle="italic"
            fontWeight="700"
            fontSize="84"
            fill="#d4b048"
            letterSpacing="2"
          >
            Telex
          </text>
        </svg>

        <div
          style={{
            marginTop: "0.6rem",
            display: "flex",
            flexWrap: "wrap",
            gap: "1.25rem",
            fontSize: "0.85rem",
            color: "#666",
          }}
        >
          <span>
            Talk: <strong style={{ color: talk ? "#1a8a25" : "#666" }}>{talk ? "ON" : "off"}</strong>{" "}
            ({talkMode})
          </span>
          <span>
            Call: <strong style={{ color: call ? "#aa2020" : "#666" }}>{call ? "ON" : "off"}</strong>
          </span>
          <span>
            Volume: <strong>{volume}</strong>
          </span>
          <span style={{ marginLeft: "auto" }}>
            T = talk · C = call · drag knob or ↑↓ = volume
          </span>
        </div>
      </div>
    </HelpShell>
  );
}
